import { useState, useMemo, useCallback, useRef, useLayoutEffect, useEffect, createContext, useContext, type ReactNode } from 'react'
import { DRAFT_LOAN_UNIT, isUnreadableLegacyDraft, letterPurpose, LOAN_SCALED_DRAFT_KEYS, pendingTermsFromDeal, ratePercent, recordedLoanAmounts, type PendingTerm, SECURITY_MEZZANINE, SECURITY_SECOND_MORTGAGE, termsFromDeal, type TermSheetDealLike } from '@/pages/termSheetTerms'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, FileSignature, Check, Minus, Plus, Maximize2, RectangleHorizontal, Loader2, Send, ShieldCheck, PenLine } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { useAntarcticaDeals } from '@/hooks/useDemoDataAccess'
import { useDemoDataSettings } from '@/context/DemoDataContext'
import { useClientProfile } from '@/hooks/useClientProfile'
import { safeBrandingPath } from '@/utils/brandingPath'
import api from '@/services/api'
import { loansService } from '@/services/loans'
import { borrowingEntity } from '@/pages/pipeline-intelligence/helpers'
import { httpStatus, loanSaveMessage } from '@/components/deal/dossier/httpStatus'
import { NO_STRUCTURE_VERSION, structuresService, type StructureUpsertBody } from '@/services/structures'
import { deskToday } from '@/utils/triggerAlerts'

/**
 * What the sheet prints where the client profile has not recorded the lender's
 * legal entity. Bracketed like every other unknown on this sheet ([Broker Name]):
 * a signed document must never carry a guessed legal name, and the bracket is
 * what a reader sees and fills before it goes out.
 */
const LENDER_NAME_PLACEHOLDER = '[Lender legal name]'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type DocStage = 'summary' | 'longform'
type WorkflowStatus = 'draft' | 'summary_sent' | 'borrower_accepted' | 'longform_issued' | 'executed'
type PreviewFit = 'screen' | 'width' | 'custom'

interface TermSheetData {
  // Broker (letter addressee — Part 1)
  brokerName: string
  brokerFirm: string
  brokerAddress: string
  brokerPhone: string
  brokerFeePct: string
  // Deal & property
  transactionType: string
  propertyClass: string
  titleType: string
  // Loan economics
  loanAmount: number
  spreadBps: number
  rateIndex: string
  rateFloorPct: string
  /** The coupon of a fixed-rate loan ("6.35%"); '' when not stated. */
  fixedRatePct: string
  term: string
  ioPeriod: string
  amortization: string
  prepaymentScope: string
  prepaymentFee: string
  extension: string
  // Fees & deposits
  origFeePct: string
  exitFeePct: string
  standbyFeePct: string
  legalExpenses: string
  goodFaithDeposit: number
  applicationFee: number
  expirationDays: number
  exclusivityDays: number
  tailDays: number
  // Recourse
  recourse: string
  guaranty: string
  netWorth: number
  liquidity: number
  // Earn-out
  earnoutIncluded: boolean
  earnoutAmount: number
  earnoutMinDY: number
  earnoutMaxLTV: number
  // Underwriting thresholds
  minInitialDY: number
  minStabilizedDY: number
  minYear1DY: number
  maxInitialLTV: number
  maxStabilizedLTV: number
  maxLTC: number
  minEquity: number
  minInitialDSCR: number
  minStabilizedDSCR: number
  // Structure
  cashManagement: string
  security: string
  rateCapStrikePct: number
  // Conditions
  conditions: Record<string, boolean>
}

// ---------------------------------------------------------------------------
// Conditions Precedent — taxonomy confirmed against the AREP summary letter
// and long-form execution term sheet. `confirmed` items have direct textual
// anchors in one or both source documents; `optional` items do not appear in
// either Antarctica template and are offered as a regional best-practice
// add-on; `construction`/`hospitality` items are asset-class-conditional
// provisions that only appear in the long-form document's bracketed
// [FOR CONSTRUCTION] / [HOTELS] alternate clauses.
// ---------------------------------------------------------------------------
interface ConditionDef {
  key: string
  label: string
  category: 'confirmed' | 'optional' | 'construction' | 'hospitality'
  defaultOn: boolean
}

const CONDITION_DEFS: ConditionDef[] = [
  { key: 'rateCap', label: 'Interest rate cap purchased & collaterally assigned at closing', category: 'confirmed', defaultOn: true },
  { key: 'escrowTI', label: 'Tax & insurance escrow established', category: 'confirmed', defaultOn: true },
  { key: 'phaseI', label: 'Phase I Environmental Site Assessment', category: 'confirmed', defaultOn: true },
  { key: 'appraisal', label: 'MAI Appraisal (as-is and as-stabilized)', category: 'confirmed', defaultOn: true },
  { key: 'pcr', label: 'Property Condition Report', category: 'confirmed', defaultOn: true },
  { key: 'titleInsurance', label: "Owner's & lender's title insurance policy, survey", category: 'confirmed', defaultOn: true },
  { key: 'zoningLetter', label: 'Zoning compliance letter / zoning report', category: 'confirmed', defaultOn: true },
  { key: 'borrowerFinancials', label: 'Indemnitor net worth & liquidity verification', category: 'confirmed', defaultOn: true },
  { key: 'orgDocs', label: 'Organizational documents & 10%+ owner chart (KYC / Patriot Act)', category: 'confirmed', defaultOn: true },
  { key: 'insurance', label: 'Evidence of property, casualty & liability insurance', category: 'confirmed', defaultOn: true },
  { key: 'leaseReview', label: 'Legal review of Material Leases', category: 'confirmed', defaultOn: true },
  { key: 'depositReceived', label: 'Good Faith Deposit & Application Fee received', category: 'confirmed', defaultOn: true },
  { key: 'equityFunded', label: 'Minimum equity funding evidenced', category: 'confirmed', defaultOn: true },
  { key: 'thresholdsMet', label: 'Debt yield, LTV, LTC & DSCR thresholds satisfied at closing, as determined by Lender', category: 'confirmed', defaultOn: true },
  { key: 'seismic', label: 'Seismic risk assessment (if applicable)', category: 'optional', defaultOn: false },
  { key: 'completionGuaranty', label: 'Completion Guaranty for capital improvement / construction work', category: 'construction', defaultOn: true },
  { key: 'gmpContract', label: 'GMP construction contract, trade buy-out & bonding', category: 'construction', defaultOn: true },
  { key: 'franchiseAgreement', label: 'Franchise agreement & franchisor comfort letter', category: 'hospitality', defaultOn: true },
  { key: 'ffeReserve', label: 'FF&E reserve funding', category: 'hospitality', defaultOn: true },
]

const WORKFLOW_STEPS: { key: WorkflowStatus; label: string }[] = [
  { key: 'draft', label: 'Drafting Summary' },
  { key: 'summary_sent', label: 'Summary Sent to Borrower' },
  { key: 'borrower_accepted', label: 'Terms Accepted' },
  { key: 'longform_issued', label: 'Long-Form Issued' },
  { key: 'executed', label: 'Executed' },
]

const ZOOM_STEP = 0.1
const ZOOM_MIN = 0.4
const ZOOM_MAX = 2

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatCurrency(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`
  return `$${v.toLocaleString()}`
}

function todayPlus(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

function todayFormatted(): string {
  return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

/**
 * The builder's starting point for a deal: what the deal record states
 * (termsFromDeal), and a platform default for everything else. The form works
 * in $MM, so the live loan amount — dollars — is converted, not reinterpreted
 * (MOD-01: $24.5M was offered as $24,500,000M). Defaults are labelled
 * "Suggested · no source" in the form until someone confirms them.
 */
/** The defaults the loan amount sets ($MM in; the deposit in dollars). */
function loanSetDefaults(loanAmount: number): Pick<TermSheetData, 'goodFaithDeposit' | 'netWorth' | 'liquidity' | 'minEquity'> {
  return {
    goodFaithDeposit: Math.round(loanAmount * 1_000_000 * 0.005),
    netWorth: Math.round(loanAmount * 0.5 * 10) / 10,
    liquidity: Math.round(loanAmount * 0.1 * 10) / 10,
    minEquity: Math.round(loanAmount * 0.15 * 10) / 10,
  }
}

function defaultFormFor(deal: TermSheetDealLike): TermSheetData {
  const stated = termsFromDeal(deal)
  const loanAmount = stated.loanAmount ?? 0
  const loanSet = loanSetDefaults(loanAmount)
  const base: TermSheetData = {
    brokerName: deal.brokerName ?? '',
    brokerFirm: '',
    brokerAddress: '',
    brokerPhone: '',
    brokerFeePct: '1.00%',
    transactionType: 'Acquisition',
    propertyClass: 'B',
    titleType: 'Fee Simple',
    loanAmount,
    spreadBps: 350,
    rateIndex: 'SOFR',
    rateFloorPct: '1.00%',
    fixedRatePct: '',
    term: '24 Months',
    // A deal that states an amortizing schedule has no full-term IO: the
    // letter read "Interest-Only Period: Full Term" beside "30-Year".
    ioPeriod: stated.amortization && stated.amortization !== 'Interest Only' ? 'None' : 'Full Term',
    amortization: 'Interest Only',
    prepaymentScope: 'Whole or Part',
    prepaymentFee: 'Greater of 1.0% or Yield Maintenance',
    extension: '2 x 12 months',
    origFeePct: '1.00%',
    exitFeePct: '0.50%',
    standbyFeePct: 'None',
    legalExpenses: 'Actual, itemized costs — payable within 2 business days of demand',
    goodFaithDeposit: loanSet.goodFaithDeposit,
    applicationFee: 25_000,
    expirationDays: 30,
    exclusivityDays: 90,
    tailDays: 180,
    recourse: 'Non-Recourse',
    guaranty: 'Standard non-recourse carve-out guaranty, plus completion and shortfall guaranties as applicable',
    netWorth: loanSet.netWorth,
    liquidity: loanSet.liquidity,
    earnoutIncluded: false,
    earnoutAmount: 0,
    earnoutMinDY: 9.0,
    earnoutMaxLTV: 65,
    minInitialDY: 7.5,
    minStabilizedDY: 9.0,
    minYear1DY: 8.0,
    maxInitialLTV: 70,
    maxStabilizedLTV: 65,
    maxLTC: 75,
    minEquity: loanSet.minEquity,
    minInitialDSCR: 0.95,
    minStabilizedDSCR: 1.20,
    cashManagement: 'Soft Lockbox — Springing on Cash Trap',
    security: 'First Mortgage Only',
    rateCapStrikePct: 4.0,
    conditions: Object.fromEntries(CONDITION_DEFS.map((c) => [c.key, c.defaultOn])),
  }
  return { ...base, ...stated }
}

/**
 * The terms the loan amount sets. When the deal states no loan amount
 * (Cranbury, S02: a 65% LTV and no amount — the NOT NULL `loans.loan_amount`
 * holds 0), none of them has a value: the letter printed "Loan Amount: $0.0M",
 * "Min. Equity: $0.0M" and "Good Faith Deposit: $0", marked Suggested.
 */
const LOAN_SET_TERMS: readonly (keyof TermSheetData)[] = ['loanAmount', 'goodFaithDeposit', 'netWorth', 'liquidity', 'minEquity']

/**
 * Where a field's value came from, for the "Suggested · no source" labels.
 * 'unstated' is a term with no value at all: the loan amount the deal does
 * not state, and the terms computed from it. It is not a Suggested default.
 */
type TermOrigin = 'deal' | 'suggested' | 'unstated' | null
const TermOriginContext = createContext<(key: keyof TermSheetData) => TermOrigin>(() => null)
/** A Suggested field that a later email states differently, awaiting review. */
const PendingTermContext = createContext<(key: keyof TermSheetData) => PendingTerm | undefined>(() => undefined)

/** Reconstruct form state from a persisted LoanStructure + its extra_terms bag. */
function formFromStructure(deal: TermSheetDealLike, s: NonNullable<Awaited<ReturnType<typeof structuresService.get>>>, recordedLoans: readonly number[] = []): TermSheetData {
  const base = defaultFormFor(deal)
  const extra = (s.extraTerms ?? {}) as Record<string, unknown>
  // A draft the pre-fix builder saved in dollars-as-millions: its loan amount
  // and the defaults computed from it are not read (nothing is rescaled).
  const unreadable: ReadonlySet<string> = new Set(isUnreadableLegacyDraft(deal, extra, recordedLoans) ? LOAN_SCALED_DRAFT_KEYS : [])
  const str = (k: string, d: string) => (typeof extra[k] === 'string' ? (extra[k] as string) : d)
  const num = (k: string, d: number) => (!unreadable.has(k) && typeof extra[k] === 'number' ? (extra[k] as number) : d)
  const bool = (k: string, d: boolean) => (typeof extra[k] === 'boolean' ? (extra[k] as boolean) : d)
  return {
    ...base,
    brokerName: str('brokerName', base.brokerName),
    brokerFirm: str('brokerFirm', base.brokerFirm),
    brokerAddress: str('brokerAddress', base.brokerAddress),
    brokerPhone: str('brokerPhone', base.brokerPhone),
    brokerFeePct: str('brokerFeePct', base.brokerFeePct),
    transactionType: str('transactionType', base.transactionType),
    propertyClass: str('propertyClass', base.propertyClass),
    titleType: str('titleType', base.titleType),
    loanAmount: num('loanAmountM', base.loanAmount),
    spreadBps: s.spreadBps ?? base.spreadBps,
    rateIndex: s.indexName ?? base.rateIndex,
    // Stored as a fraction (0.0325); the earlier builder saved a percent (3.25).
    rateFloorPct: s.rateFloor != null ? `${(ratePercent(s.rateFloor) ?? 0).toFixed(2)}%` : base.rateFloorPct,
    fixedRatePct: str('fixedRatePct', s.rateType === 'fixed' && s.allInRate != null ? `${(ratePercent(s.allInRate) ?? 0).toFixed(2)}%` : base.fixedRatePct),
    // term/ioPeriod/amortization: the backend columns store months-only integers,
    // which can't losslessly round-trip year-denominated terms (e.g. "5 Years")
    // or distinguish "25-Year" vs "30-Year" amortization at the same io_months=0.
    // extra_terms carries the exact display string as the source of truth; the
    // numeric columns remain correct for other consumers (deal_package.py etc.)
    // via toUpsertBody's conversion, they're just not used to reconstruct the UI.
    term: str('term', s.termMonths != null ? `${s.termMonths} Months` : base.term),
    ioPeriod: str('ioPeriod', s.ioMonths != null ? (s.ioMonths === 0 ? 'None' : `${s.ioMonths} Months`) : base.ioPeriod),
    amortization: str('amortization', s.amortMonths === 0 ? 'Interest Only' : base.amortization),
    prepaymentScope: str('prepaymentScope', base.prepaymentScope),
    prepaymentFee: s.prepaymentTerms ?? base.prepaymentFee,
    extension: s.extensionOptions ?? base.extension,
    origFeePct: s.originationFeeBps != null ? `${(s.originationFeeBps / 100).toFixed(2)}%` : base.origFeePct,
    exitFeePct: s.exitFeeBps != null ? `${(s.exitFeeBps / 100).toFixed(2)}%` : base.exitFeePct,
    standbyFeePct: str('standbyFeePct', base.standbyFeePct),
    legalExpenses: str('legalExpenses', base.legalExpenses),
    goodFaithDeposit: num('goodFaithDeposit', base.goodFaithDeposit),
    applicationFee: num('applicationFee', base.applicationFee),
    expirationDays: num('expirationDays', base.expirationDays),
    exclusivityDays: num('exclusivityDays', base.exclusivityDays),
    tailDays: num('tailDays', base.tailDays),
    recourse: s.recourse ?? base.recourse,
    guaranty: s.guaranty ?? base.guaranty,
    netWorth: num('netWorth', base.netWorth),
    liquidity: num('liquidity', base.liquidity),
    earnoutIncluded: bool('earnoutIncluded', base.earnoutIncluded),
    earnoutAmount: num('earnoutAmount', base.earnoutAmount),
    earnoutMinDY: num('earnoutMinDY', base.earnoutMinDY),
    earnoutMaxLTV: num('earnoutMaxLTV', base.earnoutMaxLTV),
    minInitialDY: num('minInitialDY', base.minInitialDY),
    minStabilizedDY: num('minStabilizedDY', base.minStabilizedDY),
    minYear1DY: num('minYear1DY', base.minYear1DY),
    maxInitialLTV: num('maxInitialLTV', base.maxInitialLTV),
    maxStabilizedLTV: num('maxStabilizedLTV', base.maxStabilizedLTV),
    maxLTC: num('maxLTC', base.maxLTC),
    minEquity: num('minEquity', base.minEquity),
    minInitialDSCR: num('minInitialDSCR', base.minInitialDSCR),
    minStabilizedDSCR: num('minStabilizedDSCR', base.minStabilizedDSCR),
    cashManagement: str('cashManagement', base.cashManagement),
    security: str('security', base.security),
    // The builder's own key, else the seed's `rate_cap_strike` ("4.50%"),
    // else the structure's rate_cap column (a fraction).
    rateCapStrikePct: num('rateCapStrikePct', ratePercent(extra.rateCapStrike) ?? ratePercent(s.rateCap) ?? base.rateCapStrikePct),
    conditions: (extra.conditions as Record<string, boolean>) ?? base.conditions,
  }
}

/**
 * The form fields a saved structure actually holds. Every other field is the
 * deal record's statement or a platform default, and keeps its label: a saved
 * structure used to clear every "Suggested" label, so Calloway Yards' default
 * prepayment and thresholds read as agreed terms.
 */
function structureTermKeys(deal: TermSheetDealLike, s: NonNullable<Awaited<ReturnType<typeof structuresService.get>>>, recordedLoans: readonly number[] = []): Set<string> {
  const extra = (s.extraTerms ?? {}) as Record<string, unknown>
  const keys = new Set(Object.keys(extra))
  if (extra.loanAmountM != null) keys.add('loanAmount')
  // The fields of a dollars-as-millions draft that were not read are not
  // saved terms: they keep their deal-record or Suggested label.
  if (isUnreadableLegacyDraft(deal, extra, recordedLoans)) {
    for (const k of LOAN_SCALED_DRAFT_KEYS) keys.delete(k)
    keys.delete('loanAmount')
  }
  if (extra.rateCapStrike != null || s.rateCap != null) keys.add('rateCapStrikePct')
  const columns: [unknown, keyof TermSheetData][] = [
    [s.spreadBps, 'spreadBps'],
    [s.indexName, 'rateIndex'],
    [s.rateFloor, 'rateFloorPct'],
    [s.termMonths, 'term'],
    [s.ioMonths, 'ioPeriod'],
    [s.amortMonths, 'amortization'],
    [s.prepaymentTerms, 'prepaymentFee'],
    [s.extensionOptions, 'extension'],
    [s.originationFeeBps, 'origFeePct'],
    [s.exitFeeBps, 'exitFeePct'],
    [s.recourse, 'recourse'],
    [s.guaranty, 'guaranty'],
  ]
  for (const [value, key] of columns) if (value != null) keys.add(key)
  if (s.rateType === 'fixed' && s.allInRate != null) keys.add('fixedRatePct')
  // Saving a draft is not confirming it: the fields still Suggested when the
  // draft was saved stay Suggested. Every default used to lose its mark on
  // the letter after one click of Save Draft.
  keys.delete('suggestedKeys')
  if (Array.isArray(extra.suggestedKeys)) for (const k of extra.suggestedKeys) if (typeof k === 'string') keys.delete(k)
  return keys
}

/** "24 Months" -> 24, "5 Years" -> 60. Used only for the numeric backend
 *  columns (other consumers like deal_package.py read those) — the UI itself
 *  round-trips off the exact display string stored in extra_terms. */
function toMonths(period: string): number {
  const n = parseInt(period, 10)
  if (!n) return 0
  return /year/i.test(period) ? n * 12 : n
}

function toUpsertBody(form: TermSheetData, docStage: DocStage, workflowStatus: WorkflowStatus, suggestedKeys: string[] = []): StructureUpsertBody {
  const pct = (s: string) => parseFloat(s.replace('%', '')) || 0
  const termMonths = toMonths(form.term)
  const fixed = form.rateIndex === 'Fixed (No Index)'
  // Rates are stored as FRACTIONS (0.0325), the unit the seed and backend use.
  // Saving the percent put 3.25 beside the seed's 0.0325 in the same column.
  return {
    rate_type: fixed ? 'fixed' : 'floating',
    index_name: form.rateIndex,
    spread_bps: Math.round(form.spreadBps),
    rate_floor: pct(form.rateFloorPct) / 100,
    rate_cap: form.rateCapStrikePct / 100,
    all_in_rate: fixed && pct(form.fixedRatePct) > 0 ? pct(form.fixedRatePct) / 100 : undefined,
    term_months: termMonths || undefined,
    amort_months: form.amortization === 'Interest Only' ? 0 : form.amortization === '25-Year' ? 300 : 360,
    io_months: form.ioPeriod === 'None' ? 0 : form.ioPeriod === 'Full Term' ? termMonths : toMonths(form.ioPeriod),
    extension_options: form.extension,
    prepayment_terms: form.prepaymentFee,
    recourse: form.recourse,
    guaranty: form.guaranty,
    origination_fee_bps: Math.round(pct(form.origFeePct) * 100),
    exit_fee_bps: form.exitFeePct === 'None' ? 0 : Math.round(pct(form.exitFeePct) * 100),
    extra_terms: {
      docStage,
      workflowStatus,
      loanAmountM: form.loanAmount,
      // The unit loanAmountM is in. A draft that declares it is never read
      // as one the pre-fix builder saved in dollars (nd3-33).
      loanAmountUnit: DRAFT_LOAN_UNIT,
      fixedRatePct: form.fixedRatePct,
      term: form.term,
      ioPeriod: form.ioPeriod,
      amortization: form.amortization,
      brokerName: form.brokerName,
      brokerFirm: form.brokerFirm,
      brokerAddress: form.brokerAddress,
      brokerPhone: form.brokerPhone,
      brokerFeePct: form.brokerFeePct,
      transactionType: form.transactionType,
      propertyClass: form.propertyClass,
      titleType: form.titleType,
      prepaymentScope: form.prepaymentScope,
      standbyFeePct: form.standbyFeePct,
      legalExpenses: form.legalExpenses,
      goodFaithDeposit: form.goodFaithDeposit,
      applicationFee: form.applicationFee,
      expirationDays: form.expirationDays,
      exclusivityDays: form.exclusivityDays,
      tailDays: form.tailDays,
      netWorth: form.netWorth,
      liquidity: form.liquidity,
      earnoutIncluded: form.earnoutIncluded,
      earnoutAmount: form.earnoutAmount,
      earnoutMinDY: form.earnoutMinDY,
      earnoutMaxLTV: form.earnoutMaxLTV,
      minInitialDY: form.minInitialDY,
      minStabilizedDY: form.minStabilizedDY,
      minYear1DY: form.minYear1DY,
      maxInitialLTV: form.maxInitialLTV,
      maxStabilizedLTV: form.maxStabilizedLTV,
      maxLTC: form.maxLTC,
      minEquity: form.minEquity,
      minInitialDSCR: form.minInitialDSCR,
      minStabilizedDSCR: form.minStabilizedDSCR,
      cashManagement: form.cashManagement,
      security: form.security,
      rateCapStrikePct: form.rateCapStrikePct,
      conditions: form.conditions,
      suggestedKeys,
    },
  }
}

// ---------------------------------------------------------------------------
// Small field components
// ---------------------------------------------------------------------------
function Field({ label, span, term, children }: { label: string; span?: 2; term?: keyof TermSheetData; children: React.ReactNode }) {
  const originOf = useContext(TermOriginContext)
  const pendingOf = useContext(PendingTermContext)
  const origin = term ? originOf(term) : null
  const pending = term && origin === 'suggested' ? pendingOf(term) : undefined
  return (
    <div className={`min-w-0 ${span === 2 ? 'col-span-2' : ''}`}>
      <label className="mb-0.5 flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.14em] text-stone-500">
        <span className="truncate">{label}</span>
        {origin === 'suggested' && (
          <span
            className="flex-shrink-0 rounded border border-dashed border-stone-500/50 px-1 normal-case tracking-normal text-stone-400"
            title={
              pending
                ? `A platform default. A later email states ${pending.value}${pending.source ? ` (${pending.source})` : ''}; it is held for review and not applied.`
                : 'A platform default, not a term this deal states. Confirm or change it before the letter goes out.'
            }
          >
            {/* A long stated term ("18 months minimum interest, open
                thereafter") pushed the field's own label out of the row; it
                stays in the tooltip and on the letter. */}
            {pending ? `Suggested · ${pending.value.length <= 12 ? `${pending.value} ` : ''}pending review` : 'Suggested · no source'}
          </span>
        )}
        {origin === 'unstated' && (
          <span
            className="flex-shrink-0 rounded border border-dashed border-amber-500/50 px-1 normal-case tracking-normal text-amber-300/80"
            title="The deal states no loan amount, so this term has no value yet. Enter the loan amount."
          >
            Not stated
          </span>
        )}
        {origin === 'deal' && (
          <span className="flex-shrink-0 rounded border border-emerald-500/30 px-1 normal-case tracking-normal text-emerald-300/80" title="Stated on the deal record">
            Deal record
          </span>
        )}
      </label>
      {children}
    </div>
  )
}

function TextInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className="input input-compact w-full" />
  )
}

function NumberInput({ value, onChange, suffix, step = 0.1, blank = false }: { value: number; onChange: (v: number) => void; suffix?: string; step?: number; blank?: boolean }) {
  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        value={blank ? '' : value}
        placeholder={blank ? 'Not stated' : undefined}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="input input-compact w-full"
        step={step}
      />
      {suffix && <span className="flex-shrink-0 text-[9px] text-stone-600">{suffix}</span>}
    </div>
  )
}

function SelectInput({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: string[] }) {
  // A term the deal states in its own words ("18 months minimum interest") is
  // kept as an option rather than silently shown as the first preset.
  const all = value && !options.includes(value) ? [value, ...options] : options
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="input input-compact w-full">
      {all.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  )
}

function CheckboxField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="col-span-2 flex items-center gap-1.5 py-0.5 text-[10px] text-stone-300">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-3 w-3 accent-amber-400" />
      {label}
    </label>
  )
}

function Sub({ title }: { title: string }) {
  return (
    <div className="col-span-2 mb-0.5 mt-2.5 border-t border-white/[0.04] pt-2 text-[9px] font-semibold uppercase tracking-[0.16em] text-amber-300/70 first:mt-0 first:border-t-0 first:pt-0">
      {title}
    </div>
  )
}

function computeScale(fit: PreviewFit, zoom: number, view: { w: number; h: number }, page: { w: number; h: number }): number {
  if (page.w <= 0 || page.h <= 0 || view.w <= 0 || view.h <= 0) return 1
  switch (fit) {
    case 'screen':
      return Math.min(view.w / page.w, view.h / page.h)
    case 'width':
      return view.w / page.w
    case 'custom':
      return zoom
    default: {
      const _exhaustive: never = fit
      return _exhaustive
    }
  }
}

// ---------------------------------------------------------------------------
// Preview document helpers
// ---------------------------------------------------------------------------
const SECTION_TITLE_STYLE: React.CSSProperties = {
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.1em',
  color: '#8b6427',
  borderBottom: '1px solid #d8c8ad',
  paddingBottom: '3px',
  marginBottom: '6px',
}

function DocSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-3">
      <div style={SECTION_TITLE_STYLE}>{title}</div>
      {children}
    </div>
  )
}

/**
 * One row of the letter. `terms` names the form fields the row prints; when
 * any of them is still a platform default nobody has confirmed, the row says
 * so on the letter itself — the preview used to print "Greater of 1.0% or
 * Yield Maintenance" and a 4.00% cap strike as if they were agreed terms.
 */
function KV({ label, value, terms }: { label: string; value: ReactNode; terms?: (keyof TermSheetData)[] }) {
  const originOf = useContext(TermOriginContext)
  const pendingOf = useContext(PendingTermContext)
  const suggestedTerms = (terms ?? []).filter((t) => originOf(t) === 'suggested')
  const suggested = suggestedTerms.length > 0
  const pending = suggestedTerms.map((t) => pendingOf(t)).filter((p): p is PendingTerm => p != null)
  // A term with no value prints as not stated — never as $0 or a Suggested $0.
  const unstated = (terms ?? []).some((t) => originOf(t) === 'unstated')
  return (
    <tr>
      <td style={{ width: '44%', color: '#5f5f5f', paddingRight: '10px', verticalAlign: 'top' }}>{label}</td>
      <td style={{ fontWeight: 500 }}>
        {unstated ? (
          <span style={{ fontStyle: 'italic', fontWeight: 400, color: '#9a6b00' }} title="The deal states no loan amount. Enter it before the letter goes out.">
            [Not stated — the deal states no loan amount]
          </span>
        ) : value}
        {suggested && !unstated && (
          <span
            style={{ marginLeft: '6px', fontSize: '9px', fontStyle: 'italic', fontWeight: 400, color: '#9a6b00' }}
            title="A platform default, not a term this deal states. Confirm or change it before the letter goes out."
          >
            {pending.length
              ? `[Suggested — not confirmed; ${pending.map((p) => p.value).join(', ')} pending review]`
              : '[Suggested — not confirmed]'}
          </span>
        )}
      </td>
    </tr>
  )
}

function KVTable({ children }: { children: ReactNode }) {
  return (
    <table className="w-full" style={{ fontSize: '11px', lineHeight: '1.55' }}>
      <tbody>{children}</tbody>
    </table>
  )
}

function SignatureBlock({ role, name }: { role: string; name: string }) {
  return (
    <div>
      <p style={{ color: '#5f5f5f', fontWeight: 600, fontSize: '10px', letterSpacing: '0.06em' }}>{role}:</p>
      <p style={{ marginTop: '2px' }}>{name}</p>
      <div style={{ borderBottom: '1px solid #171717', width: '100%', marginTop: '16px' }} />
      <p style={{ color: '#5f5f5f', fontSize: '9px', marginTop: '3px' }}>Authorized Signatory</p>
      <div style={{ borderBottom: '1px solid #171717', width: '60%', marginTop: '10px' }} />
      <p style={{ color: '#5f5f5f', fontSize: '9px', marginTop: '3px' }}>Date</p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function TermSheet() {
  const deals = useAntarcticaDeals('term-sheet')
  // The lender is the workspace's own client: its mark, name and legal entity come from
  // the client profile, never from this file. Optional lender overlays (C27d) win
  // when set; otherwise legalName / name / logoDark print as they already did.
  const client = useClientProfile()
  const lenderLegalName =
    (client.lenderLegalName ?? '').trim() || client.legalName || LENDER_NAME_PLACEHOLDER
  const lenderBrand = (client.lenderName ?? '').trim() || client.name
  // Only a same-origin /branding file is ever loaded as the sheet's mark.
  const clientLogo =
    safeBrandingPath((client.letterheadLogo ?? '').trim()) || safeBrandingPath(client.logoDark)
  const { isModuleDemoEnabled } = useDemoDataSettings()
  const demoEnabled = isModuleDemoEnabled('term-sheet')
  const queryClient = useQueryClient()

  // The deal a term sheet was asked for from elsewhere (the dossier's Term
  // Sheet entry, C27d): /term-sheet?loan=<id>. Not ?deal=, which opens the
  // dossier overlay on every page.
  const [searchParams] = useSearchParams()
  const requestedLoanId = searchParams.get('loan') || null
  const [selectedDealId, setSelectedDealId] = useState(requestedLoanId ?? deals[0]?.id ?? '')
  // A deal that was asked for by id and is not in the book is said so — never
  // silently swapped for the first deal, whose terms would then read as the
  // one the person clicked from.
  const requestedMissing =
    requestedLoanId !== null && selectedDealId === requestedLoanId && !deals.some((d) => d.id === requestedLoanId)
  const deal = useMemo(
    () => deals.find((d) => d.id === selectedDealId) ?? (requestedMissing ? undefined : deals[0]),
    [deals, selectedDealId, requestedMissing],
  )
  useEffect(() => {
    if (requestedLoanId) setSelectedDealId(requestedLoanId)
  }, [requestedLoanId])

  const [docStage, setDocStage] = useState<DocStage>('summary')
  const [workflowStatus, setWorkflowStatus] = useState<WorkflowStatus>('draft')
  const [dirty, setDirty] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  // A save refused because someone changed these terms since they were
  // opened (C23b): the page offers their terms instead of retrying blind.
  const [termsConflict, setTermsConflict] = useState(false)

  const [fit, setFit] = useState<PreviewFit>('width')
  const [zoom, setZoom] = useState(1)
  const [viewSize, setViewSize] = useState({ w: 1, h: 1 })
  const [pageSize, setPageSize] = useState({ w: 1, h: 1 })

  const viewportRef = useRef<HTMLDivElement>(null)
  const pageRef = useRef<HTMLDivElement>(null)

  const [form, setForm] = useState<TermSheetData>(() => defaultFormFor(deal ?? { loanAmount: 0, rate: '' }))
  // Fields a person has edited this session, and the action awaiting an
  // explicit confirmation (the two that move the deal's stage).
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set())
  const [confirming, setConfirming] = useState<null | 'summary' | 'executed'>(null)
  // Chris Young, 26 Oct 2026 (CY-10): a signed term sheet advances to Term Sheet
  // Signed only once the deposit wire for third-party costs is confirmed. The
  // date travels with the stage move; without it the execution is recorded and
  // the deal stays where it is.
  const [wireDate, setWireDate] = useState('')

  // Live (non-demo) loans have a real structure record to load; demo mock
  // deals do not exist server-side, so we never query for them.
  const structureQuery = useQuery({
    queryKey: ['loan-structure', deal?.id],
    queryFn: () => structuresService.get(deal!.id),
    enabled: !!deal && !demoEnabled,
    staleTime: 30_000,
  })
  // Every loan amount the deal has held: a draft the pre-fix builder saved
  // holds one of them in dollars, even after the loan was changed (nd3-33).
  const loanHistoryQuery = useQuery({
    queryKey: ['loan-field-history', deal?.id, 'loan_amount'],
    queryFn: () => loansService.getFieldHistory(deal!.id, 'loan_amount'),
    enabled: !!deal && !demoEnabled && !!structureQuery.data,
    staleTime: 30_000,
  })
  const recordedLoans = useMemo(() => recordedLoanAmounts(loanHistoryQuery.data), [loanHistoryQuery.data])
  const loanHistorySettled = !structureQuery.data || loanHistoryQuery.isFetched

  // The version of the terms this page is editing (C23b), sent as If-Match on
  // every save. It follows each load and each successful save; until this
  // deal's terms have loaded it is unknown, and a save then names none (the
  // server refuses it) rather than another deal's.
  const [baseVersion, setBaseVersion] = useState<string | null>(null)
  useEffect(() => {
    if (demoEnabled) return
    if (!structureQuery.isFetched) {
      setBaseVersion(null)
      return
    }
    setBaseVersion(structureQuery.data ? (structureQuery.data.version ?? null) : NO_STRUCTURE_VERSION)
  }, [deal?.id, demoEnabled, structureQuery.data, structureQuery.isFetched])

  useEffect(() => {
    if (!deal) return
    if (!demoEnabled && structureQuery.data && !loanHistorySettled) return
    if (!demoEnabled && structureQuery.data) {
      setForm(formFromStructure(deal, structureQuery.data, recordedLoans))
      const extra = (structureQuery.data.extraTerms ?? {}) as Record<string, unknown>
      if (typeof extra.docStage === 'string') setDocStage(extra.docStage as DocStage)
      if (typeof extra.workflowStatus === 'string') setWorkflowStatus(extra.workflowStatus as WorkflowStatus)
    } else if (!demoEnabled && structureQuery.isFetched && !structureQuery.data) {
      setForm(defaultFormFor(deal))
      setWorkflowStatus('draft')
    } else if (demoEnabled) {
      setForm(defaultFormFor(deal))
      setWorkflowStatus(deal.stage === 'Term Sheet Signed' ? 'executed' : deal.stage === 'Term Sheet Issued' ? 'summary_sent' : 'draft')
    }
    setDirty(false)
    setActionError(null)
    setTermsConflict(false)
    setTouched(new Set())
    setConfirming(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.id, demoEnabled, structureQuery.data, structureQuery.isFetched, loanHistorySettled])

  const updateField = useCallback(<K extends keyof TermSheetData>(field: K, value: TermSheetData[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    setTouched((prev) => (prev.has(field) ? prev : new Set(prev).add(field)))
    setDirty(true)
  }, [])

  // A saved structure is terms a person has already set; otherwise each field
  // is either stated on the deal record or a platform default (R: Suggested).
  const savedKeys = useMemo(
    () => (!demoEnabled && structureQuery.data && deal ? structureTermKeys(deal, structureQuery.data, recordedLoans) : new Set<string>()),
    [demoEnabled, structureQuery.data, deal, recordedLoans],
  )
  const statedKeys = useMemo(() => new Set(Object.keys(deal ? termsFromDeal(deal) : {})), [deal])
  // Entering the loan amount carries the defaults it sets, unless a person
  // set them: a loan typed into an unstated deal otherwise left a $0 deposit.
  const updateLoanAmount = useCallback((v: number) => {
    const follow = loanSetDefaults(v)
    setForm((prev) => {
      const next = { ...prev, loanAmount: v }
      for (const k of Object.keys(follow) as (keyof typeof follow)[]) {
        if (!touched.has(k) && !savedKeys.has(k)) next[k] = follow[k]
      }
      return next
    })
    setTouched((prev) => (prev.has('loanAmount') ? prev : new Set(prev).add('loanAmount')))
    setDirty(true)
  }, [touched, savedKeys])
  const loanUnstated = !(form.loanAmount > 0)
  const originOf = useCallback(
    (key: keyof TermSheetData): TermOrigin =>
      savedKeys.has(key) || touched.has(key)
        ? null
        : statedKeys.has(key)
          ? 'deal'
          : loanUnstated && LOAN_SET_TERMS.includes(key)
            ? 'unstated'
            : 'suggested',
    [savedKeys, touched, statedKeys, loanUnstated],
  )
  const pendingTerms = useMemo(() => (deal ? pendingTermsFromDeal(deal) : {}), [deal])
  const pendingOf = useCallback(
    (key: keyof TermSheetData): PendingTerm | undefined => (pendingTerms as Partial<Record<string, PendingTerm>>)[key],
    [pendingTerms],
  )
  const hasSuggested = useMemo(
    () => (Object.keys(form) as (keyof TermSheetData)[]).some((k) => originOf(k) === 'suggested'),
    [form, originOf],
  )

  const toggleCondition = useCallback((key: string) => {
    setForm((prev) => ({ ...prev, conditions: { ...prev.conditions, [key]: !prev.conditions[key] } }))
    setDirty(true)
  }, [])

  const handleDealChange = useCallback((id: string) => {
    setSelectedDealId(id)
    setDocStage('summary')
  }, [])

  const visibleConditions = useMemo(() => {
    return CONDITION_DEFS.filter((c) => {
      if (c.category === 'construction') return deal?.type === 'Construction'
      if (c.category === 'hospitality') return deal?.type === 'Hospitality'
      return true
    })
  }, [deal?.type])

  const activeConditionLabels = useMemo(
    () => visibleConditions.filter((c) => form.conditions[c.key]).map((c) => c.label),
    [visibleConditions, form.conditions],
  )

  const saveMutation = useMutation({
    mutationFn: (nextStatus: WorkflowStatus) =>
      structuresService.upsert(
        deal!.id,
        // An unstated term is no more confirmed by a save than a default is.
        toUpsertBody(form, docStage, nextStatus, (Object.keys(form) as (keyof TermSheetData)[]).filter((k) => originOf(k) === 'suggested' || originOf(k) === 'unstated')),
        baseVersion,
      ),
    onSuccess: (saved) => {
      // The next save names the version this one produced, even before the
      // refetch lands — a second quick save is not a conflict with oneself.
      if (saved?.version) setBaseVersion(saved.version)
      queryClient.invalidateQueries({ queryKey: ['loan-structure', deal?.id] })
    },
  })

  const stageMutation = useMutation({
    // Sends the version this page loaded (CMP-04): a stage someone else set
    // since is refused with 409 and explained, not silently overwritten.
    mutationFn: ({ stage, depositWireDate }: { stage: 'Term Sheet Issued' | 'Term Sheet Signed'; depositWireDate?: string }) =>
      loansService.update(
        deal!.id,
        depositWireDate
          ? // Only the one key: the server merges it into the deal's fields.
            { stage, customFields: { deposit_wire_date: depositWireDate } }
          : { stage },
        deal!.updatedAt ?? deal!.createdAt ?? null,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] })
    },
  })

  const advance = useCallback(
    async (
      next: WorkflowStatus,
      loanStage?: 'Term Sheet Issued' | 'Term Sheet Signed',
      depositWireDate?: string,
    ) => {
      setActionError(null)
      setTermsConflict(false)
      let writingTerms = false
      try {
        if (!demoEnabled && deal) {
          if (loanStage) {
            // The terms save below can be refused (C23b) — after the stage
            // had moved. Terms someone changed since they were opened are
            // caught here first, so a refused step moves nothing.
            const latest = await structuresService.get(deal.id)
            const latestVersion = latest ? (latest.version ?? null) : NO_STRUCTURE_VERSION
            if (latestVersion !== baseVersion) {
              setTermsConflict(true)
              setActionError(
                'These terms were changed after you opened them. Nothing was saved and the deal’s stage was not moved. Load their terms, then re-apply yours.',
              )
              return
            }
            // The stage move goes first: it is the write the server can refuse
            // (a newer change by someone else, CMP-04; a role past Triage). If
            // it is refused, the term sheet must not record the step either,
            // or the page reads "Summary Sent" on a deal whose stage never moved.
            await stageMutation.mutateAsync({ stage: loanStage, depositWireDate })
          }
          writingTerms = true
          await saveMutation.mutateAsync(next)
        }
        setWorkflowStatus(next)
        setDirty(false)
      } catch (err) {
        if (writingTerms && httpStatus(err) === 409) setTermsConflict(true)
        setActionError(loanSaveMessage(err))
      }
    },
    [baseVersion, demoEnabled, deal, saveMutation, stageMutation],
  )

  // Their terms replace the form (the reload clears the conflict and every
  // unsaved edit); the person re-applies their own from there.
  const loadCurrentTerms = useCallback(() => {
    setTermsConflict(false)
    setActionError(null)
    void structureQuery.refetch()
  }, [structureQuery])

  const handleSaveDraft = useCallback(() => advance(workflowStatus), [advance, workflowStatus])
  const handleSendSummary = useCallback(() => advance('summary_sent', 'Term Sheet Issued'), [advance])
  const handleMarkAccepted = useCallback(() => advance('borrower_accepted'), [advance])
  const handleIssueLongform = useCallback(() => advance('longform_issued'), [advance])
  const handleMarkExecuted = useCallback(
    (depositWireDate: string) => advance('executed', 'Term Sheet Signed', depositWireDate),
    [advance],
  )
  // Executed, but the deposit wire has not landed: the execution is recorded and
  // the deal is not advanced (Chris's rule). Entering the wire date later on the
  // deal's Workflow fields lets it move to Term Sheet Signed.
  const handleExecutedAwaitingWire = useCallback(() => advance('executed'), [advance])
  const openExecutedConfirm = useCallback(() => {
    // The API's keys arrive camel-cased (`customFields.depositWireDate`); the
    // clocks block carries the same date already normalised to a day.
    const recorded = deal?.dealClocks?.depositWireDate ?? deal?.customFields?.depositWireDate
    setWireDate(typeof recorded === 'string' ? recorded.slice(0, 10) : '')
    setConfirming('executed')
  }, [deal])

  /**
   * Printing a quote letter is an EVENT (register Q12).
   *
   * Chris's example: "when a quote letter is requested and printed, a report of
   * the triage performed on that deal generates automatically and goes to the
   * person who printed it." The scheduler has been able to fire on an event for
   * a while; nothing ever told it one had happened, because this button was
   * `window.print()` with no server contact at all.
   *
   * The print is never blocked on the recording. A reporting side-effect must
   * not stand between a person and the document they asked for, so the failure
   * is logged and the dialog opens regardless.
   */
  const handleDownloadPdf = useCallback(() => {
    if (!demoEnabled && deal) {
      const printId =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`
      api
        .post('/reports/quote-letter/printed', {
          loan_id: deal.id,
          doc_stage: docStage,
          print_id: printId,
        })
        .catch((err) => {
          console.warn('quote-letter print was not recorded for reporting', err)
        })
    }
    window.print()
  }, [demoEnabled, deal, docStage])

  const scale = useMemo(() => computeScale(fit, zoom, viewSize, pageSize), [fit, zoom, viewSize, pageSize])

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    const page = pageRef.current
    if (!viewport || !page) return
    const measure = () => {
      const styles = getComputedStyle(viewport)
      const padX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight)
      const padY = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom)
      setViewSize({ w: Math.max(viewport.clientWidth - padX, 1), h: Math.max(viewport.clientHeight - padY, 1) })
      setPageSize({ w: page.offsetWidth, h: page.offsetHeight })
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(viewport)
    ro.observe(page)
    return () => ro.disconnect()
  }, [deal, form, docStage, activeConditionLabels.length])

  const setFitScreen = useCallback(() => setFit('screen'), [])
  const setFitWidth = useCallback(() => { setFit('width'); setZoom(1) }, [])
  const bumpZoom = useCallback((delta: number) => {
    const base = fit === 'custom' ? zoom : scale
    const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((base + delta) * 10) / 10))
    setFit('custom')
    setZoom(next)
  }, [fit, zoom, scale])

  if (requestedMissing) {
    return (
      <div className="page-fill p-4">
        <div>
          <div className="terminal-kicker">Term Sheet Builder</div>
          <p className="mt-1 text-xs text-stone-500">Generate indicative and long-form term sheets from deal data</p>
        </div>
        <div className="panel mt-3">
          <div role="alert" className="panel-body px-4 py-10 text-center">
            <p className="text-[12px] text-stone-300">
              Deal {requestedLoanId} is not in the live book, so there is no term sheet to build from it here.
            </p>
            <p className="mt-1 text-[11px] text-stone-500">
              A Dead or archived deal is not offered. Reopen it first, or build a term sheet for another deal.
            </p>
            {deals.length > 0 && (
              <button
                type="button"
                onClick={() => handleDealChange(deals[0].id)}
                className="btn-secondary mt-3 px-2.5 py-1 text-[11px]"
              >
                Choose another deal
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (!deal) {
    return (
      <div className="page-fill p-4">
        <div>
          <div className="terminal-kicker">Term Sheet Builder</div>
          <p className="mt-1 text-xs text-stone-500">Generate indicative and long-form term sheets from deal data</p>
        </div>
        <div className="panel mt-3">
          <div className="panel-body px-4 py-10 text-center">
            <p className="text-[12px] text-stone-400">No deals available to build a term sheet from.</p>
            <p className="mt-1 text-[11px] text-stone-600">
              Add a deal from Intake — email, file upload, or manual entry — and it will appear here.
            </p>
          </div>
        </div>
      </div>
    )
  }

  const zoomPct = Math.round(scale * 100)
  const paperWidth = Math.max(viewSize.w, 1)
  const currentStepIndex = WORKFLOW_STEPS.findIndex((s) => s.key === workflowStatus)
  const canIssueLongform = currentStepIndex >= WORKFLOW_STEPS.findIndex((s) => s.key === 'borrower_accepted')
  const saving = saveMutation.isPending || stageMutation.isPending

  return (
    <TermOriginContext.Provider value={originOf}><PendingTermContext.Provider value={pendingOf}>
    <div className="page-fill gap-2 p-3">
      <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-2">
        <div>
          <div className="terminal-kicker">Term Sheet Builder</div>
          <p className="mt-0.5 text-[11px] text-stone-500">
            Part 1 indicative summary → Part 2 long-form execution document, in one workflow
            {demoEnabled && <span className="ml-2 rounded-full border border-amber-400/20 bg-amber-400/[0.07] px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-amber-300/80">Demo — not persisted</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSaveDraft} disabled={saving} className="btn-secondary flex items-center gap-1.5 disabled:opacity-50">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <FileSignature size={13} />}
            Save Draft
          </button>
          {dirty && !saving && <span className="text-[10px] text-amber-300/70">Unsaved changes</span>}
          {docStage === 'summary' && workflowStatus === 'draft' && (
            <button onClick={() => setConfirming('summary')} disabled={saving} className="btn-primary flex items-center gap-1.5 disabled:opacity-50">
              <Send size={13} /> Mark Summary Sent…
            </button>
          )}
          {workflowStatus === 'summary_sent' && (
            <button onClick={handleMarkAccepted} disabled={saving} className="btn-primary flex items-center gap-1.5 disabled:opacity-50">
              <Check size={13} /> Mark Terms Accepted
            </button>
          )}
          {workflowStatus === 'borrower_accepted' && docStage === 'longform' && (
            <button onClick={handleIssueLongform} disabled={saving} className="btn-primary flex items-center gap-1.5 disabled:opacity-50">
              <ShieldCheck size={13} /> Issue Long-Form for Execution
            </button>
          )}
          {workflowStatus === 'longform_issued' && (
            <button onClick={openExecutedConfirm} disabled={saving} className="btn-primary flex items-center gap-1.5 disabled:opacity-50">
              <PenLine size={13} /> Mark Executed…
            </button>
          )}
          {workflowStatus === 'executed' && (
            <span className="flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1.5 text-[11px] font-medium text-emerald-300">
              <Check size={13} /> Executed
            </span>
          )}
          <button onClick={handleDownloadPdf} className="btn-secondary flex items-center gap-1.5">
            <Download size={13} /> Download PDF
          </button>
        </div>
      </div>

      {confirming && (
        <div role="alertdialog" aria-label="Confirm stage change" className="flex-shrink-0 rounded-md border border-amber-400/30 bg-amber-400/[0.06] px-3 py-2 text-[11px] text-stone-300">
          <p>
            {confirming === 'summary'
              ? `This records that you sent the summary terms to the borrower yourself, saves these terms, and moves ${deal.property} to Term Sheet Issued. The platform does not email or send anything.`
              : `This records that the long-form term sheet was executed and saves these terms. ${deal.property} moves to Term Sheet Signed only once the deposit wire for third-party costs is confirmed.`}
            {hasSuggested && (
              <span className="ml-1 text-amber-300">
                Terms marked Suggested · no source are platform defaults that nobody has confirmed.
              </span>
            )}
          </p>
          {confirming === 'executed' && (
            <label className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-stone-400">Deposit wire received on</span>
              <input
                type="date"
                value={wireDate}
                max={deskToday()}
                onChange={(e) => setWireDate(e.target.value)}
                className="rounded border border-stone-700 bg-black/40 px-1.5 py-0.5 text-[11px] text-stone-200"
              />
              <span className="text-stone-500">Leave empty if the wire has not landed yet.</span>
            </label>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {confirming === 'summary' ? (
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  setConfirming(null)
                  void handleSendSummary()
                }}
                className="btn-primary px-2.5 py-1 text-[11px] disabled:opacity-50"
              >
                Confirm — summary was sent
              </button>
            ) : (
              <>
                <button
                  type="button"
                  disabled={saving || !wireDate || wireDate > deskToday()}
                  onClick={() => {
                    setConfirming(null)
                    void handleMarkExecuted(wireDate)
                  }}
                  className="btn-primary px-2.5 py-1 text-[11px] disabled:opacity-50"
                >
                  Confirm — executed, wire received
                </button>
                <button
                  type="button"
                  disabled={saving || Boolean(wireDate)}
                  onClick={() => {
                    setConfirming(null)
                    void handleExecutedAwaitingWire()
                  }}
                  className="btn-secondary px-2.5 py-1 text-[11px] disabled:opacity-50"
                >
                  Executed — wire not yet received
                </button>
              </>
            )}
            <button type="button" onClick={() => setConfirming(null)} className="btn-secondary px-2.5 py-1 text-[11px]">
              Cancel
            </button>
          </div>
        </div>
      )}

      {actionError && (
        <div role="alert" className="flex flex-shrink-0 items-center gap-2 rounded-md border border-red-500/30 bg-red-500/[0.06] px-2.5 py-1.5 text-[11px] text-red-300">
          <span className="min-w-0 flex-1">{actionError}</span>
          {termsConflict && (
            <button type="button" onClick={loadCurrentTerms} className="btn-secondary flex-shrink-0 px-2.5 py-1 text-[11px]">
              Load their terms
            </button>
          )}
        </div>
      )}

      {/* Workflow stepper */}
      <div className="flex flex-shrink-0 items-center gap-1 overflow-x-auto rounded-md border border-amber-400/10 bg-black/20 px-2.5 py-1.5 scrollbar-thin">
        {WORKFLOW_STEPS.map((step, i) => {
          const done = i < currentStepIndex
          const active = i === currentStepIndex
          return (
            <div key={step.key} className="flex flex-shrink-0 items-center gap-1">
              <div
                className={`flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] ${
                  active
                    ? 'bg-amber-400/15 text-amber-200'
                    : done
                      ? 'text-emerald-300/80'
                      : 'text-stone-600'
                }`}
              >
                <span className={`flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full border text-[8px] ${
                  active ? 'border-amber-400/50 bg-amber-400/20' : done ? 'border-emerald-500/40 bg-emerald-500/15' : 'border-stone-700'
                }`}>
                  {done ? <Check size={9} /> : i + 1}
                </span>
                {step.label}
              </div>
              {i < WORKFLOW_STEPS.length - 1 && <div className="h-px w-3 flex-shrink-0 bg-stone-700" />}
            </div>
          )
        })}
      </div>

      <div className="grid min-h-0 flex-1 gap-2 overflow-hidden xl:grid-cols-[minmax(300px,0.95fr)_1.05fr]">
        <div className="card flex min-h-0 flex-col overflow-hidden px-3 py-2.5">
          <div className="mb-2 flex flex-shrink-0 items-center justify-between gap-2">
            <div className="terminal-kicker">Term Sheet Fields</div>
            <div className="flex rounded-md border border-amber-400/10 p-0.5 text-[10px]">
              <button
                type="button"
                onClick={() => setDocStage('summary')}
                className={`rounded px-2 py-0.5 ${docStage === 'summary' ? 'bg-amber-400/15 text-amber-200' : 'text-stone-500 hover:text-stone-300'}`}
              >
                Part 1 · Summary
              </button>
              <button
                type="button"
                onClick={() => setDocStage('longform')}
                className={`rounded px-2 py-0.5 ${docStage === 'longform' ? 'bg-amber-400/15 text-amber-200' : 'text-stone-500 hover:text-stone-300'}`}
              >
                Part 2 · Long-Form
              </button>
            </div>
          </div>

          <div className="mb-2 flex-shrink-0">
            <Field label="Deal">
              <select aria-label="Deal" value={deal.id} onChange={(e) => handleDealChange(e.target.value)} className="input input-compact w-full">
                {deals.map((d) => (
                  <option key={d.id} value={d.id}>{d.id} - {d.property}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="gold-divider mb-2 flex-shrink-0" />

          {hasSuggested && (
            <p className="mb-2 flex-shrink-0 rounded-md border border-dashed border-stone-500/40 px-2 py-1.5 text-[10px] leading-relaxed text-stone-400">
              Terms marked <span className="text-emerald-300/80">Deal record</span> come from this deal. Terms marked{' '}
              <span className="text-stone-300">Suggested · no source</span> are platform defaults the deal does not
              state — confirm or change each before the letter goes out.
            </p>
          )}

          <TermOriginContext.Provider value={originOf}><PendingTermContext.Provider value={pendingOf}>
          <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin pr-0.5">
            <div className="grid grid-cols-2 gap-x-2.5 gap-y-1.5">
              {docStage === 'summary' && (
                <>
                  <Sub title="Broker (Letter Addressee)" />
                  <Field label="Broker Name" term="brokerName"><TextInput value={form.brokerName} onChange={(v) => updateField('brokerName', v)} /></Field>
                  <Field label="Broker Firm" term="brokerFirm"><TextInput value={form.brokerFirm} onChange={(v) => updateField('brokerFirm', v)} /></Field>
                  <Field label="Broker Address" span={2} term="brokerAddress"><TextInput value={form.brokerAddress} onChange={(v) => updateField('brokerAddress', v)} /></Field>
                  <Field label="Broker Phone" term="brokerPhone"><TextInput value={form.brokerPhone} onChange={(v) => updateField('brokerPhone', v)} /></Field>
                </>
              )}

              {docStage === 'longform' && !canIssueLongform && (
                <div className="col-span-2 rounded-md border border-amber-400/10 bg-black/20 px-2 py-1.5 text-[10px] leading-relaxed text-stone-500">
                  Drafting ahead of acceptance — issuance unlocks once the borrower has accepted the Part 1 summary terms.
                </div>
              )}

              <Sub title="Deal & Property" />
              <Field label="Transaction Type" term="transactionType">
                <SelectInput value={form.transactionType} onChange={(v) => updateField('transactionType', v)} options={['Acquisition', 'Refinance', 'Construction']} />
              </Field>
              <Field label="Property Class" term="propertyClass">
                <SelectInput value={form.propertyClass} onChange={(v) => updateField('propertyClass', v)} options={['A', 'B', 'C']} />
              </Field>
              {docStage === 'longform' && (
                <Field label="Title" span={2} term="titleType">
                  <SelectInput value={form.titleType} onChange={(v) => updateField('titleType', v)} options={['Fee Simple', 'Ground Lease']} />
                </Field>
              )}

              <Sub title="Loan Economics" />
              <Field label="Loan Amount" term="loanAmount"><NumberInput value={form.loanAmount} onChange={updateLoanAmount} suffix="M" blank={originOf('loanAmount') === 'unstated'} /></Field>
              <Field label="Index" term="rateIndex">
                <SelectInput value={form.rateIndex} onChange={(v) => updateField('rateIndex', v)} options={['SOFR', 'UST 10-Year', 'Fixed (No Index)']} />
              </Field>
              {form.rateIndex === 'Fixed (No Index)' ? (
                <Field label="Fixed Rate" term="fixedRatePct"><TextInput value={form.fixedRatePct} onChange={(v) => updateField('fixedRatePct', v)} /></Field>
              ) : (
                <>
                  <Field label="Spread" term="spreadBps"><NumberInput value={form.spreadBps} onChange={(v) => updateField('spreadBps', v)} suffix="bps" step={5} /></Field>
                  <Field label="Rate Floor" term="rateFloorPct">
                    <SelectInput value={form.rateFloorPct} onChange={(v) => updateField('rateFloorPct', v)} options={['0.50%', '0.75%', '1.00%', '1.25%', '1.50%', '2.00%']} />
                  </Field>
                </>
              )}
              <Field label="Term" term="term">
                <SelectInput value={form.term} onChange={(v) => updateField('term', v)} options={['12 Months', '18 Months', '24 Months', '36 Months', '5 Years', '7 Years', '10 Years']} />
              </Field>
              <Field label="IO Period" term="ioPeriod">
                <SelectInput value={form.ioPeriod} onChange={(v) => updateField('ioPeriod', v)} options={['None', '12 Months', '24 Months', '36 Months', 'Full Term']} />
              </Field>
              <Field label="Amortization" term="amortization">
                <SelectInput value={form.amortization} onChange={(v) => updateField('amortization', v)} options={['Interest Only', '25-Year', '30-Year']} />
              </Field>
              <Field label="Extension" term="extension">
                <SelectInput value={form.extension} onChange={(v) => updateField('extension', v)} options={['None', '1 x 12 months', '2 x 12 months', '1 x 6 months', '2 x 6 months']} />
              </Field>
              <Field label="Prepayment Scope" term="prepaymentScope">
                <SelectInput value={form.prepaymentScope} onChange={(v) => updateField('prepaymentScope', v)} options={['Whole or Part', 'Full Only']} />
              </Field>
              <Field label="Prepayment Fee" term="prepaymentFee">
                <SelectInput value={form.prepaymentFee} onChange={(v) => updateField('prepaymentFee', v)} options={['Greater of 1.0% or Yield Maintenance', 'Minimum Interest Amount', 'None (Open)']} />
              </Field>

              <Sub title="Fees & Deposits" />
              <Field label="Origination Fee" term="origFeePct">
                <SelectInput value={form.origFeePct} onChange={(v) => updateField('origFeePct', v)} options={['0.50%', '0.75%', '1.00%', '1.25%', '1.50%']} />
              </Field>
              <Field label="Exit Fee" term="exitFeePct">
                <SelectInput value={form.exitFeePct} onChange={(v) => updateField('exitFeePct', v)} options={['None', '0.10%', '0.25%', '0.50%']} />
              </Field>
              <Field label="Stand-by Fee" term="standbyFeePct">
                <SelectInput value={form.standbyFeePct} onChange={(v) => updateField('standbyFeePct', v)} options={['None', '0.10%', '0.25%', '0.50%']} />
              </Field>
              <Field label="Expiration" term="expirationDays"><NumberInput value={form.expirationDays} onChange={(v) => updateField('expirationDays', v)} suffix="days" step={1} /></Field>
              <Field label="Good Faith Deposit" term="goodFaithDeposit"><NumberInput value={form.goodFaithDeposit} onChange={(v) => updateField('goodFaithDeposit', v)} suffix="$" step={1000} blank={originOf('goodFaithDeposit') === 'unstated'} /></Field>
              {docStage === 'longform' && (
                <Field label="Application Fee" term="applicationFee"><NumberInput value={form.applicationFee} onChange={(v) => updateField('applicationFee', v)} suffix="$" step={1000} /></Field>
              )}
              <Field label="Diligence / Legal Expenses" span={2} term="legalExpenses"><TextInput value={form.legalExpenses} onChange={(v) => updateField('legalExpenses', v)} /></Field>

              <Sub title="Recourse & Guaranty" />
              <Field label="Recourse" term="recourse">
                <SelectInput value={form.recourse} onChange={(v) => updateField('recourse', v)} options={['Non-Recourse', 'Partial Recourse', 'Full Recourse']} />
              </Field>
              {docStage === 'longform' && (
                <>
                  <Field label="Indemnitor Net Worth" term="netWorth"><NumberInput value={form.netWorth} onChange={(v) => updateField('netWorth', v)} suffix="M" blank={originOf('netWorth') === 'unstated'} /></Field>
                  <Field label="Indemnitor Liquidity" term="liquidity"><NumberInput value={form.liquidity} onChange={(v) => updateField('liquidity', v)} suffix="M" blank={originOf('liquidity') === 'unstated'} /></Field>
                </>
              )}
              <Field label="Guaranty" span={2} term="guaranty"><TextInput value={form.guaranty} onChange={(v) => updateField('guaranty', v)} /></Field>

              <Sub title="Earn-Out" />
              <CheckboxField label="Earn-out included" checked={form.earnoutIncluded} onChange={(v) => updateField('earnoutIncluded', v)} />
              {form.earnoutIncluded && (
                <>
                  <Field label="Earn-Out Amount" term="earnoutAmount"><NumberInput value={form.earnoutAmount} onChange={(v) => updateField('earnoutAmount', v)} suffix="M" /></Field>
                  <Field label="Min Debt Yield" term="earnoutMinDY"><NumberInput value={form.earnoutMinDY} onChange={(v) => updateField('earnoutMinDY', v)} suffix="%" /></Field>
                  <Field label="Max LTV" term="earnoutMaxLTV"><NumberInput value={form.earnoutMaxLTV} onChange={(v) => updateField('earnoutMaxLTV', v)} suffix="%" /></Field>
                </>
              )}

              <Sub title="Underwriting Thresholds" />
              <Field label="Min Initial DY" term="minInitialDY"><NumberInput value={form.minInitialDY} onChange={(v) => updateField('minInitialDY', v)} suffix="%" /></Field>
              <Field label="Min Stabilized DY" term="minStabilizedDY"><NumberInput value={form.minStabilizedDY} onChange={(v) => updateField('minStabilizedDY', v)} suffix="%" /></Field>
              {docStage === 'longform' && (
                <Field label="Min Year 1 DY"><NumberInput value={form.minYear1DY} onChange={(v) => updateField('minYear1DY', v)} suffix="%" /></Field>
              )}
              <Field label="Max Initial LTV" term="maxInitialLTV"><NumberInput value={form.maxInitialLTV} onChange={(v) => updateField('maxInitialLTV', v)} suffix="%" /></Field>
              <Field label="Max Stabilized LTV" term="maxStabilizedLTV"><NumberInput value={form.maxStabilizedLTV} onChange={(v) => updateField('maxStabilizedLTV', v)} suffix="%" /></Field>
              <Field label="Max LTC" term="maxLTC"><NumberInput value={form.maxLTC} onChange={(v) => updateField('maxLTC', v)} suffix="%" /></Field>
              <Field label="Min Equity" term="minEquity"><NumberInput value={form.minEquity} onChange={(v) => updateField('minEquity', v)} suffix="M" blank={originOf('minEquity') === 'unstated'} /></Field>
              {docStage === 'longform' && (
                <>
                  <Field label="Min Initial DSCR" term="minInitialDSCR"><NumberInput value={form.minInitialDSCR} onChange={(v) => updateField('minInitialDSCR', v)} suffix="x" step={0.01} /></Field>
                  <Field label="Min Stabilized DSCR" term="minStabilizedDSCR"><NumberInput value={form.minStabilizedDSCR} onChange={(v) => updateField('minStabilizedDSCR', v)} suffix="x" step={0.01} /></Field>
                </>
              )}

              <Sub title="Structure" />
              <Field label="Cash Management" span={2} term="cashManagement">
                <SelectInput value={form.cashManagement} onChange={(v) => updateField('cashManagement', v)} options={['Hard Lockbox — In Place at Closing', 'Soft Lockbox — Springing on Cash Trap', 'None']} />
              </Field>
              <Field label="Security" span={2} term="security">
                <SelectInput value={form.security} onChange={(v) => updateField('security', v)} options={['First Mortgage Only', 'First Mortgage + 100% Equity Pledge', SECURITY_SECOND_MORTGAGE, SECURITY_MEZZANINE]} />
              </Field>
              <Field label="Rate Cap Strike" term="rateCapStrikePct"><NumberInput value={form.rateCapStrikePct} onChange={(v) => updateField('rateCapStrikePct', v)} suffix="%" /></Field>

              {docStage === 'longform' && (
                <>
                  <Sub title="Broker Fee & Exclusivity" />
                  <Field label="Broker Fee" term="brokerFeePct">
                    <SelectInput value={form.brokerFeePct} onChange={(v) => updateField('brokerFeePct', v)} options={['None', '0.50%', '0.75%', '1.00%', '1.25%', '1.50%']} />
                  </Field>
                  <Field label="Exclusivity Period" term="exclusivityDays"><NumberInput value={form.exclusivityDays} onChange={(v) => updateField('exclusivityDays', v)} suffix="days" step={1} /></Field>
                  <Field label="Tail Period" term="tailDays"><NumberInput value={form.tailDays} onChange={(v) => updateField('tailDays', v)} suffix="days" step={1} /></Field>
                </>
              )}
            </div>

            {docStage === 'summary' && (
              <div className="mt-3 rounded-md border border-amber-400/10 bg-black/20 px-2.5 py-2 text-[10px] leading-relaxed text-stone-500">
                Closing conditions are not itemized in the indicative summary — matching the {client.name || 'client'} template's
                "other standard provisions typical of loans of this type" language. The full Conditions Precedent
                checklist appears on the Part 2 Long-Form document.
              </div>
            )}

            {docStage === 'longform' && (
              <div className="mt-3">
                <div className="mb-1.5 flex items-center justify-between">
                  <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-amber-300/70">Conditions Precedent</div>
                  <span className="font-mono text-[10px] text-stone-500">{activeConditionLabels.length}/{visibleConditions.length}</span>
                </div>
                <div className="grid grid-cols-1 gap-y-0.5">
                  {visibleConditions.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => toggleCondition(c.key)}
                      className="flex w-full items-start gap-1.5 rounded px-1 py-0.5 text-left hover:bg-white/[0.03]"
                    >
                      <span
                        className={`mt-0.5 flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded border ${
                          form.conditions[c.key] ? 'border-amber-400/40 bg-amber-400/20' : 'border-stone-700 bg-black/40'
                        }`}
                      >
                        {form.conditions[c.key] && <Check size={9} className="text-amber-300" />}
                      </span>
                      <span className="text-[10px] leading-tight text-stone-300">
                        {c.label}
                        {c.category === 'optional' && <span className="ml-1 text-stone-600">(not in template — optional)</span>}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          </PendingTermContext.Provider></TermOriginContext.Provider>
        </div>

        <div className="card flex min-h-0 flex-col overflow-hidden">
          <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-x-2 gap-y-1.5 border-b border-amber-400/10 px-3 py-1.5">
            <div className="flex items-center gap-2">
              <div className="terminal-kicker">{docStage === 'summary' ? 'Summary Term Sheet Preview' : 'Long-Form Term Sheet Preview'}</div>
            </div>
            <div className="flex flex-shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={setFitScreen}
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] ${
                  fit === 'screen' ? 'border-amber-400/30 bg-amber-400/[0.10] text-amber-200' : 'border-amber-400/10 text-stone-400 hover:border-amber-400/20 hover:text-stone-200'
                }`}
              >
                <Maximize2 size={10} /> Fit to screen
              </button>
              <button
                type="button"
                onClick={setFitWidth}
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] ${
                  fit === 'width' ? 'border-amber-400/30 bg-amber-400/[0.10] text-amber-200' : 'border-amber-400/10 text-stone-400 hover:border-amber-400/20 hover:text-stone-200'
                }`}
              >
                <RectangleHorizontal size={10} /> Fit to width
              </button>
              <div className="ml-1 flex items-center rounded-md border border-amber-400/10">
                <button type="button" onClick={() => bumpZoom(-ZOOM_STEP)} disabled={fit === 'custom' && zoom <= ZOOM_MIN} className="px-1.5 py-1 text-stone-400 hover:text-amber-200 disabled:opacity-30" aria-label="Zoom out">
                  <Minus size={11} />
                </button>
                <span className="min-w-[2.6rem] text-center font-mono text-[10px] text-stone-300">{zoomPct}%</span>
                <button type="button" onClick={() => bumpZoom(ZOOM_STEP)} disabled={fit === 'custom' && zoom >= ZOOM_MAX} className="px-1.5 py-1 text-stone-400 hover:text-amber-200 disabled:opacity-30" aria-label="Zoom in">
                  <Plus size={11} />
                </button>
              </div>
            </div>
          </div>

          <div ref={viewportRef} className={`min-h-0 flex-1 p-2 ${fit === 'screen' ? 'overflow-hidden' : 'overflow-auto scrollbar-thin'}`} id="term-sheet-print-root">
            <div className="mx-auto overflow-hidden" style={{ width: pageSize.w * scale, height: pageSize.h * scale }}>
              <div
                ref={pageRef}
                className="box-border rounded-lg shadow-2xl px-6 py-4"
                style={{
                  width: paperWidth,
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                  background: 'linear-gradient(180deg, #faf6ed 0%, #f5f0e3 100%)',
                  color: '#171717',
                  fontFamily: "'Georgia', 'Times New Roman', serif",
                }}
              >
                <div className="text-center mb-3">
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '6px' }}>
                    {/* The page is a light sheet, so the client's mark for light backgrounds. */}
                    {clientLogo ? (
                      <img src={clientLogo} alt={lenderBrand || client.name} style={{ height: '40px', width: 'auto', objectFit: 'contain' }} />
                    ) : (
                      <div style={{ fontSize: '16px', fontWeight: 700, letterSpacing: '0.04em' }}>{lenderBrand || client.name}</div>
                    )}
                  </div>
                  <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, #c49a43, transparent)', margin: '6px 0' }} />
                </div>

                {docStage === 'summary' ? (
                  <>
                    <div style={{ fontSize: '10px', color: '#5f5f5f', display: 'flex', justifyContent: 'space-between' }}>
                      <span>{todayFormatted()}</span>
                      <span style={{ fontStyle: 'italic' }}>[DRAFT]</span>
                    </div>
                    <div style={{ fontSize: '11px', marginTop: '8px', lineHeight: '1.5' }}>
                      <p>{form.brokerName || '[Broker Name]'}</p>
                      <p>{form.brokerFirm || '[Broker Firm]'}</p>
                      <p>{form.brokerAddress || '[Broker Address]'}</p>
                      <p>{form.brokerPhone || '[Broker Phone]'}</p>
                      <p style={{ marginTop: '8px' }}>RE: {deal.property} — {deal.location}</p>
                      <p style={{ marginTop: '8px' }}>Dear {form.brokerName ? form.brokerName.split(' ')[0] : 'Broker'}:</p>
                      <p style={{ marginTop: '8px' }}>
                        Pursuant to our previous discussions, the following represents our indicative terms for a
                        proposed loan facility {letterPurpose(form.transactionType, deal.property, deal.type, deal.location)} (the &ldquo;Loan&rdquo;) from {lenderLegalName} and/or its affiliates (&ldquo;Lender&rdquo;).
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="text-center mb-1">
                    <div style={{ fontSize: '11px', fontWeight: 600 }}>{deal.sponsor}</div>
                    <div style={{ fontSize: '11px', color: '#5f5f5f' }}>{deal.property} | {deal.location}</div>
                  </div>
                )}

                <div className="text-center mb-3 mt-2">
                  <div style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.08em', color: '#171717' }}>
                    {docStage === 'summary' ? 'SUMMARY OF INDICATIVE TERMS' : 'SUMMARY OF INDICATIVE TERMS AND CONDITIONS'}
                  </div>
                  <div style={{ fontSize: '10px', color: '#5f5f5f', marginTop: '2px' }}>
                    {docStage === 'summary' ? 'Part 1 — Confidential, For Discussion Purposes Only' : 'Part 2 — Long-Form Execution Term Sheet'}
                  </div>
                </div>

                <DocSection title="PROPERTY INFORMATION">
                  <KVTable>
                    <KV label="Property:" value={<span style={{ fontWeight: 600 }}>{deal.property}</span>} />
                    <KV label="Location:" value={deal.location} />
                    <KV label="Property Type:" value={`${deal.type} — Class ${form.propertyClass}`} terms={['propertyClass']} />
                    <KV label="Transaction Type:" value={form.transactionType} terms={['transactionType']} />
                    {docStage === 'longform' && <KV label="Title:" value={form.titleType} terms={['titleType']} />}
                    <KV label="Borrower:" value={borrowingEntity(deal) || 'Borrowing entity not stated'} />
                    <KV label="Sponsor:" value={deal.sponsor} />
                  </KVTable>
                </DocSection>

                <DocSection title="PROPOSED LOAN TERMS">
                  <KVTable>
                    <KV label="Loan Amount:" value={<span style={{ fontWeight: 600 }}>${form.loanAmount.toFixed(1)}M</span>} terms={['loanAmount']} />
                    {form.rateIndex === 'Fixed (No Index)' ? (
                      <KV label="Interest Rate:" value={`${form.fixedRatePct || '[Fixed rate not stated]'} Fixed`} terms={['rateIndex', 'fixedRatePct']} />
                    ) : (
                      <KV label="Interest Rate:" value={`${form.rateIndex} + ${form.spreadBps} bps (${form.rateFloorPct} floor)`} terms={['rateIndex', 'spreadBps', 'rateFloorPct']} />
                    )}
                    <KV label="Term:" value={form.term} terms={['term']} />
                    <KV label="Interest-Only Period:" value={form.ioPeriod} terms={['ioPeriod']} />
                    <KV label="Amortization:" value={form.amortization} terms={['amortization']} />
                    <KV label="Extension Options:" value={form.extension} terms={['extension']} />
                    <KV label="Prepayment:" value={`${form.prepaymentScope} — ${form.prepaymentFee}`} terms={['prepaymentScope', 'prepaymentFee']} />
                    <KV label="Recourse:" value={form.recourse} terms={['recourse']} />
                    {docStage === 'longform' && (
                      <>
                        <KV label="Indemnitor Net Worth:" value={`$${form.netWorth.toFixed(1)}M`} terms={['netWorth']} />
                        <KV label="Indemnitor Liquidity:" value={`$${form.liquidity.toFixed(1)}M`} terms={['liquidity']} />
                      </>
                    )}
                    <KV label="Guaranty:" value={form.guaranty} terms={['guaranty']} />
                    <KV label="Cash Management:" value={form.cashManagement} terms={['cashManagement']} />
                    <KV label="Security:" value={form.security} terms={['security']} />
                    {/* A fixed-rate loan buys no interest-rate cap. */}
                    {form.rateIndex !== 'Fixed (No Index)' && (
                      <KV label="Rate Cap Strike:" value={`${form.rateCapStrikePct.toFixed(2)}%`} terms={['rateCapStrikePct']} />
                    )}
                  </KVTable>
                </DocSection>

                {form.earnoutIncluded && (
                  <DocSection title="EARN-OUT">
                    <KVTable>
                      <KV label="Earn-Out Amount:" value={`$${form.earnoutAmount.toFixed(1)}M`} terms={['earnoutAmount']} />
                      <KV label="Minimum Debt Yield:" value={`${form.earnoutMinDY.toFixed(2)}%`} terms={['earnoutMinDY']} />
                      <KV label="Maximum LTV:" value={`${form.earnoutMaxLTV.toFixed(1)}%`} terms={['earnoutMaxLTV']} />
                    </KVTable>
                  </DocSection>
                )}

                <DocSection title="UNDERWRITING THRESHOLDS">
                  <KVTable>
                    <KV label="Min. Initial Debt Yield:" value={`${form.minInitialDY.toFixed(2)}%`} terms={['minInitialDY']} />
                    <KV label="Min. Stabilized Debt Yield:" value={`${form.minStabilizedDY.toFixed(2)}%`} terms={['minStabilizedDY']} />
                    {docStage === 'longform' && <KV label="Min. Year 1 Debt Yield:" value={`${form.minYear1DY.toFixed(2)}%`} terms={['minYear1DY']} />}
                    <KV label="Max. Initial LTV:" value={`${form.maxInitialLTV.toFixed(1)}%`} terms={['maxInitialLTV']} />
                    <KV label="Max. Stabilized LTV:" value={`${form.maxStabilizedLTV.toFixed(1)}%`} terms={['maxStabilizedLTV']} />
                    <KV label="Max. LTC:" value={`${form.maxLTC.toFixed(1)}%`} terms={['maxLTC']} />
                    <KV label="Min. Equity:" value={`$${form.minEquity.toFixed(1)}M`} terms={['minEquity']} />
                    {docStage === 'longform' && (
                      <>
                        <KV label="Min. Initial DSCR:" value={`${form.minInitialDSCR.toFixed(2)}x`} terms={['minInitialDSCR']} />
                        <KV label="Min. Stabilized DSCR:" value={`${form.minStabilizedDSCR.toFixed(2)}x`} terms={['minStabilizedDSCR']} />
                      </>
                    )}
                  </KVTable>
                </DocSection>

                <DocSection title="FEES & DEPOSITS">
                  <KVTable>
                    <KV label="Origination Fee:" value={loanUnstated ? form.origFeePct : `${form.origFeePct} (${formatCurrency(form.loanAmount * 1_000_000 * (parseFloat(form.origFeePct) || 0) / 100)})`} terms={['origFeePct']} />
                    <KV label="Exit Fee:" value={form.exitFeePct} terms={['exitFeePct']} />
                    <KV label="Stand-by Fee:" value={form.standbyFeePct} terms={['standbyFeePct']} />
                    <KV label="Diligence / Legal Expenses:" value={form.legalExpenses} terms={['legalExpenses']} />
                    <KV label="Good Faith Deposit:" value={formatCurrency(form.goodFaithDeposit)} terms={['goodFaithDeposit']} />
                    {docStage === 'longform' && <KV label="Application Fee:" value={formatCurrency(form.applicationFee)} terms={['applicationFee']} />}
                    {docStage === 'longform' && <KV label="Broker Fee:" value={form.brokerFeePct} terms={['brokerFeePct']} />}
                  </KVTable>
                </DocSection>

                {docStage === 'summary' ? (
                  <DocSection title="OTHER CONDITIONS">
                    <p style={{ fontSize: '11px', lineHeight: '1.5', color: '#171717' }}>
                      Other standard provisions typical of loans of this type. A detailed Conditions Precedent
                      checklist and full legal terms will accompany the long-form Term Sheet issued upon
                      Borrower&rsquo;s acceptance of these indicative terms.
                    </p>
                  </DocSection>
                ) : (
                  <>
                    <DocSection title="CONDITIONS PRECEDENT TO CLOSING">
                      <p style={{ color: '#5f5f5f', marginBottom: '4px', fontSize: '11px' }}>The following conditions must be satisfied prior to closing:</p>
                      <ol style={{ paddingLeft: '18px', color: '#171717', fontSize: '11px', lineHeight: '1.5' }}>
                        {activeConditionLabels.map((label, i) => <li key={i}>{label}</li>)}
                      </ol>
                    </DocSection>

                    <DocSection title="EXCLUSIVITY">
                      <p style={{ fontSize: '11px', lineHeight: '1.5', color: '#171717' }}>
                        Lender shall have the exclusive right to provide the Loan for {form.exclusivityDays} days following
                        execution of this Term Sheet (the &ldquo;Exclusivity Period&rdquo;), with a {form.tailDays}-day tail period
                        applicable to any substitute transaction thereafter.
                      </p>
                    </DocSection>

                    <DocSection title="SCHEDULE B — FINANCIAL REPORTING">
                      <table className="w-full" style={{ fontSize: '9.5px', lineHeight: '1.4', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #d8c8ad', color: '#8b6427', textAlign: 'left' }}>
                            <th style={{ padding: '2px 6px 2px 0' }}>Deliverable</th>
                            <th style={{ padding: '2px 6px' }}>Frequency</th>
                            <th style={{ padding: '2px 6px' }}>Prepared By</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td style={{ padding: '2px 6px 2px 0' }}>Property statements &amp; rent roll</td><td style={{ padding: '2px 6px' }}>Monthly / quarterly, 30 days</td><td style={{ padding: '2px 6px' }}>Borrower</td></tr>
                          <tr><td style={{ padding: '2px 6px 2px 0' }}>CPA-reviewed property statements</td><td style={{ padding: '2px 6px' }}>Annually, 60 days</td><td style={{ padding: '2px 6px' }}>Borrower / CPA</td></tr>
                          <tr><td style={{ padding: '2px 6px 2px 0' }}>Annual budget &amp; operating plan</td><td style={{ padding: '2px 6px' }}>30 days before year end</td><td style={{ padding: '2px 6px' }}>Borrower</td></tr>
                          <tr><td style={{ padding: '2px 6px 2px 0' }}>Indemnitor statements</td><td style={{ padding: '2px 6px' }}>Monthly / quarterly, 30 days</td><td style={{ padding: '2px 6px' }}>Indemnitor</td></tr>
                        </tbody>
                      </table>
                    </DocSection>
                  </>
                )}

                <DocSection title="EXPIRATION">
                  <p style={{ fontSize: '11px', lineHeight: '1.5', color: '#171717' }}>
                    {docStage === 'summary'
                      ? <>This indicative term sheet is valid through <strong>{todayPlus(form.expirationDays)}</strong> ({form.expirationDays} calendar days from issuance).</>
                      : <>Sponsor must countersign and return this Term Sheet, with all requisite deposits, within five (5) business days of the date above, or it is null and void.</>}
                  </p>
                </DocSection>

                <DocSection title="ACCEPTANCE">
                  <div className="grid grid-cols-2 gap-6" style={{ marginTop: '8px' }}>
                    <SignatureBlock role="LENDER" name={lenderLegalName} />
                    {docStage === 'summary' ? (
                      <SignatureBlock role="BORROWER" name={borrowingEntity(deal) || '[Borrowing entity to be confirmed]'} />
                    ) : (
                      <SignatureBlock role="SPONSOR" name={deal.sponsor} />
                    )}
                  </div>
                  {docStage === 'longform' && (
                    <div style={{ marginTop: '16px' }}>
                      <SignatureBlock role="PRINCIPAL" name="[Principal Name]" />
                    </div>
                  )}
                </DocSection>

                <div style={{ marginTop: '10px', borderTop: '2px solid #c49a43', paddingTop: '8px' }}>
                  <div style={{ fontSize: '8px', color: '#8b6427', letterSpacing: '0.1em', textAlign: 'center', lineHeight: '1.5' }}>
                    CONFIDENTIAL &mdash; Provided for discussion purposes only and does not constitute a commitment to lend.
                    Subject to satisfactory due diligence, internal credit approval, and execution of definitive loan documentation.
                    {lenderBrand || LENDER_NAME_PLACEHOLDER} reserves the right to modify or withdraw these terms at any time prior to execution.
                  </div>
                  <div style={{ fontSize: '8px', color: '#8c8c8c', textAlign: 'center', marginTop: '6px' }}>
                    Generated by Helios CapitalMarketsOS &mdash; {todayFormatted()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body * { visibility: hidden; }
          #term-sheet-print-root, #term-sheet-print-root * { visibility: visible; }
          #term-sheet-print-root { position: absolute; left: 0; top: 0; width: 100%; overflow: visible !important; }
        }
      `}</style>
    </div>
    </PendingTermContext.Provider></TermOriginContext.Provider>
  )
}
